import { useEffect, useState } from "react";
import type { Bootstrap, Capabilities } from "../lib/api";
import type { ConnectionSettings } from "../connections/api";
import { settingsDestinations } from "../connections/directory";
import { declaredModelCheck, modelSettlement } from "../connections/modelSettlement";
import { readAgentView } from "./api";

/**
 * Whether an Agent can be talked to yet, from what it answers about itself.
 *
 * Two facts, both the Agent's own. Its owner's seat is known: `owner.present`
 * in its bootstrap, null while its record is still being replayed. And a turn
 * has a chat model to call: `modelSettlement`, the rule its own settings page
 * draws, applied to the declarations in that bootstrap, the default stored
 * where they say, and its `models` registry. A connected socket is neither --
 * the directory's `online` says only that a connection exists, and an owner
 * taken in on that met the Agent's own loading page, or a conversation that
 * could not answer.
 *
 * `waiting` is everything not known yet: no reading, a seat still being
 * recovered, declarations that have not all arrived. `no-model` is the seat
 * known and no model to call; `namespace` is the settings page that declared
 * the check, where a model is chosen or connected.
 */
export type AgentReadiness =
  | { state: "waiting" }
  | { state: "ready" }
  | { state: "no-model"; namespace: string };

const WAITING: AgentReadiness = { state: "waiting" };
/** Between two readings, for as long as the answer is not `ready`. */
const INTERVAL_MS = 2000;

/** One reading, or undefined when this round could not tell. */
async function readReadiness(id: string, signal: AbortSignal): Promise<AgentReadiness | undefined> {
  const boot = await readAgentView<Bootstrap>(id, "/api/bootstrap", signal);
  if (boot?.owner?.present !== true) return WAITING;
  const check = declaredModelCheck(settingsDestinations(Array.isArray(boot.panels) ? boot.panels : []));
  if (!check) return WAITING;
  const [connection, capabilities] = await Promise.all([
    readAgentView<ConnectionSettings>(id, `/api/connections/${encodeURIComponent(check.defaultNamespace)}`, signal),
    readAgentView<Capabilities>(id, "/api/capabilities", signal),
  ]);
  const models = capabilities?.registries?.find(registry => registry.kind === "models");
  if (!models || models.problem || !Array.isArray(models.entries) || !connection?.values) return undefined;
  return modelSettlement(connection.values, models.entries, check).settled
    ? { state: "ready" } : { state: "no-model", namespace: check.namespace };
}

/**
 * Reads `id` until it is ready, and stops when `id` goes (null) or the page
 * does. A round that could not tell keeps the last answer rather than
 * flickering back to waiting; a hidden tab skips its reading.
 */
export function useAgentReadiness(id: string | null): AgentReadiness {
  const [reading, setReading] = useState<{ id: string; readiness: AgentReadiness } | null>(null);
  useEffect(() => {
    setReading(null);
    if (!id) return;
    const controller = new AbortController();
    let timer: number | undefined;
    const round = async () => {
      let answer: AgentReadiness | undefined;
      if (document.visibilityState !== "hidden") {
        try {
          answer = await readReadiness(id, controller.signal);
        } catch {
          answer = undefined;
        }
      }
      if (controller.signal.aborted) return;
      if (answer) setReading({ id, readiness: answer });
      if (answer?.state !== "ready") timer = window.setTimeout(() => { void round(); }, INTERVAL_MS);
    };
    void round();
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [id]);
  return reading?.id === id ? reading.readiness : WAITING;
}
